"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { Search, Star, Loader2, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils/cn";

type SearchResult = {
  schemeCode: string;
  schemeName: string;
  amc?: string;
  category?: string;
  hasReport?: boolean;
};

/** Derive Direct/Regular plan type from the scheme name. */
function derivePlanType(name: string): string | null {
  const n = name.toLowerCase();
  if (/\bdirect\b/.test(n)) return "Direct";
  if (/\bregular\b/.test(n)) return "Regular";
  return null;
}

type FundSearchBarProps = {
  onSelect: (result: { schemeCode: string; schemeName: string }) => void;
};

export function FundSearchBar({ onSelect }: FundSearchBarProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `/api/funds/search?q=${encodeURIComponent(q.trim())}`
      );
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setResults(json.data);
        setApiUnavailable(!!json.apiUnavailable);
        setOpen(true);
      }
    } catch {
      setApiUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, []);

  function handleInputChange(value: string) {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value), 300);
  }

  function handleAnalyze(result: SearchResult, e: React.MouseEvent) {
    // Stop propagation so this doesn't bubble up and close the dropdown
    // before the parent's onSelect handler gets the event.
    e.preventDefault();
    e.stopPropagation();
    setQuery(result.schemeName);
    setOpen(false);
    onSelect({ schemeCode: result.schemeCode, schemeName: result.schemeName });
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search Indian mutual funds..."
          className="w-full bg-surface border border-surface-light/30 rounded-lg pl-9 pr-10 py-2.5 text-sm text-foreground placeholder:text-dim focus:outline-none focus:border-blue/50 transition"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
      </div>

      {/* Dropdown results. Clicking the row itself does nothing —
          generation only starts when the user clicks "Analyze →". This
          prevents accidental generation on misclicks. */}
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-surface border border-surface-light/30 rounded-xl shadow-lg max-h-80 overflow-y-auto">
          {apiUnavailable && (
            <div className="px-4 py-2 bg-gold/10 border-b border-gold/20 text-gold text-[11px]">
              Fund data temporarily unavailable: showing previously analyzed funds only
            </div>
          )}
          {results.map((r) => (
            <div
              key={r.schemeCode}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3",
                "border-b border-surface-light/10 last:border-0"
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-foreground text-sm font-medium truncate">
                    {r.schemeName}
                  </span>
                  {r.hasReport && (
                    <Star className="w-3 h-3 text-gold shrink-0 fill-gold" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {r.amc && (
                    <span className="text-dim text-[11px]">{r.amc}</span>
                  )}
                  {r.category && (
                    <>
                      <span className="text-dim text-[11px]">&bull;</span>
                      <span className="bg-blue/15 text-blue text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                        {r.category}
                      </span>
                    </>
                  )}
                  {derivePlanType(r.schemeName) && (
                    <span className="text-dim text-[11px]">
                      {derivePlanType(r.schemeName)}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={(e) => handleAnalyze(r, e)}
                className="shrink-0 flex items-center gap-1 bg-blue/10 hover:bg-blue/20 text-blue text-xs font-semibold px-3 py-1.5 rounded-lg transition cursor-pointer border-none"
              >
                Analyze
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {open && !loading && results.length === 0 && query.trim().length >= 2 && (
        <div className="absolute z-50 w-full mt-1 bg-surface border border-surface-light/30 rounded-xl shadow-lg p-4">
          <p className="text-dim text-sm text-center">
            {apiUnavailable
              ? "Fund data temporarily unavailable. Please try again later."
              : "No funds found"}
          </p>
        </div>
      )}
    </div>
  );
}
